import type { Theme } from '@personalizehq/schemas/ir';

// The app theme is NULL until first written — the API invents no default, correctly (a token
// dictionary is the customer's design language, not ours). But `createDoc(theme)` needs one, so the
// dashboard ships this and writes it on first use.
//
// Two laws are visible in the shape and are not negotiable:
//   - every colour is a light/dark PAIR (a raw hex has no dark variant — SPEC §4);
//   - fonts are BUNDLED, never system, IN PRODUCTION. `family` is a placeholder until the author
//     uploads a font asset and the token gets a `src` (SPEC §4.1).
//
// F5: the seed named `Inter`, which NOTHING loads — no `src`, no bundled asset — so the canvas
// measured and painted a *system fallback* (Times, before `fix/editor-usability` stopped the fall to
// serif), while the device would pick yet another fallback. Every wrap point on the canvas was wrong,
// and wrong differently than on the phone: the canvas's whole "what you see is what the SDK draws"
// claim was false at the type layer. Naming `system-ui` — a family that ACTUALLY EXISTS on every
// platform — makes the canvas paint the family it names, and on the common author-on-mac →
// preview-on-iPhone path both resolve to the SAME face (SF), so the wrap points finally agree.
// ponytail: a starter default, not the answer. Cross-platform (SF vs Roboto vs Segoe) it still
// diverges — the real fix is a bundled font asset, which is why `src` exists. Left as the knob.
export const SEED_THEME: Theme = {
  colors: {
    'color.bg': { light: '#ffffff', dark: '#18181b' },
    'color.surface': { light: '#f4f4f5', dark: '#27272a' },
    'color.text': { light: '#18181b', dark: '#fafafa' },
    'color.muted': { light: '#71717a', dark: '#a1a1aa' },
    'color.primary': { light: '#4f46e5', dark: '#818cf8' },
    'color.on_primary': { light: '#ffffff', dark: '#0b0b0f' },
  },
  fonts: {
    'font.title': { family: 'system-ui', size: 24, weight: 700, line_height: 1.25, max_scale: 1.4 },
    'font.body': { family: 'system-ui', size: 16, weight: 400, line_height: 1.5, max_scale: 1.6 },
    'font.caption': { family: 'system-ui', size: 13, weight: 400, line_height: 1.4, max_scale: 1.6 },
    'font.button': { family: 'system-ui', size: 16, weight: 600, line_height: 1.2, max_scale: 1.4 },
  },
  // Component tokens COMPOSE the foundation tokens above (ADR 0014): a `radii` radius and, on the
  // primary CTA, an `elevations` lift. `padding` stays literal (spacing is not tokenized).
  buttons: {
    primary: {
      bg: 'color.primary',
      fg: 'color.on_primary',
      radius: 'radius.md',
      elevation: 'elevation.low',
      padding: { top: 12, end: 20, bottom: 12, start: 20 },
    },
    secondary: {
      bg: 'color.surface',
      fg: 'color.text',
      radius: 'radius.md',
      padding: { top: 12, end: 20, bottom: 12, start: 20 },
    },
    // `bg: null` is a real variant, not an omission — a text-only button.
    ghost: {
      bg: null,
      fg: 'color.primary',
      radius: 'radius.md',
      padding: { top: 12, end: 16, bottom: 12, start: 16 },
    },
  },
  // Paint-token families (ADR 0014). A starter corner + elevation language; gradients are
  // brand-specific, so the seed offers one subtle example rather than a scale.
  radii: {
    'radius.sm': 6,
    'radius.md': 12,
    'radius.lg': 20,
    'radius.pill': 999,
  },
  elevations: {
    'elevation.low': { y: 2, blur: 8, color: 'color.text' },
    'elevation.high': { y: 8, blur: 24, color: 'color.text' },
  },
  gradients: {
    'gradient.brand': { kind: 'linear', angle: 135, stops: ['color.primary', 'color.surface'] },
  },
};
